/// <reference path="input.ts" />
/// <reference path="fetch.ts" />

/**
 * <dl>
 * <dt>属性</dt>
 * <dd>option-url: 選択候補を取得するためのURL</dd>
 * <dd>option-list: 選択候補を取得した際のリストのプロパティ名</dd>
 * <dd>option-label: 選択候補を取得した際のラベルプロパティ名（デフォルト値はlabel）</dd>
 * <dd>option-value: 選択候補を取得した際の値プロパティ名（デフォルト値はvalue）</dd>
 * <dd>target: 対象エレメントのセレクタ</dd>
 * <dd>target-attr: 対象アトリビュート</dd>
 * <dd>target-value: 対象アトリビュートに設定する値（{value}は設定値と置換される）</dd>
 * </dl>
 * その他の属性はKoyoriInputに依存します。
 */
class KoyoriSelect extends KoyoriInput {

	static get observedAttributes() {
		return ['label', 'message', 'value', 'values', 'option-url'].concat(this.inputAttributes);
	}

	static get inputAttributes() {
		return ['name', 'disabled', 'autocomplete', 'list', 'maxlength', 'pattern', 'placeholder', 'readonly', 'inputmode', 'multiple'];
	}

	constructor() {
		super();
		this.rebuild();
		this.addEventListener('change', () => {
			if (this.getAttribute('target') != null) {
				let target = document.querySelector(this.getAttribute('target'));
				target.setAttribute(this.getAttribute('target-attr'), this.getAttribute('target-value').replace(/\{value\}/g, this.value));
			}
		});
	}

	connectedCallback(): void {
		super.connectedCallback();
	}

	/**
	 * @return 変更イベントを取得する属性のリスト
	 */
	protected getObservedAttributes(): string[] {
		return KoyoriSelect.observedAttributes;
	}

	/**
	 * @return inputタグに設定する属性のリスト
	 */
	protected getInputAttributes(): string[] {
		return KoyoriSelect.inputAttributes;
	}

	valueChanged(value: string): void {
		(<HTMLSelectElement>this.inputElement).value = value;
	}

	get values(): string[] {
		let values: string[] = [];
		KoyoriElement.children(this.inputElement).forEach(child => {
			let option = (<HTMLOptionElement>child);
			if (option.selected) {
				values.push(option.getAttribute('value'));
			}
		});
		return values;
	}

	set values(values: string[]) {
		// TODO 不具合のため動作しない
		KoyoriElement.children(this.inputElement).forEach(child => {
			let option = (<HTMLOptionElement>child);
			let value = child.getAttribute('value');
			if (values.indexOf(value) >= 0) {
				option.selected = true;
			} else {
				option.selected = false;
			}
		});
	}

	/**
	 * optionタグを再構築します。
	 */
	rebuild(): void {
		KoyoriElement.children(this).forEach(child => {
			if (child.tagName != 'OPTION') {
				return;
			}
			if (child.innerHTML == '') {
				child.innerHTML = '&nbsp;'
				if (child.getAttribute('value') == null) {
					child.setAttribute('value', '');
				}
			}
			this.inputElement.appendChild(child);
		});
	}

	/**
	 * optionタグを除去します。
	 */
	clear(): void {
		this.inputElement.textContent = '';
	}

	valuesChanged(value: string): void {
		if (value == null) {
			this.values = [];
		} else {
			this.values = value.split(' ');
		}
	}

	optionUrlChanged(value: string): void {
		KoyoriFetch.fetch(this.getAttribute('option-url'), {
			credentials: 'include'
		}, {
			success: response => {
				response.json().then(data => {
					if (data == null) {
						return;
					}
					KoyoriElement.querySelectorAll(this.inputElement, '.koy-input-fetch-option').forEach(element => {
						this.inputElement.removeChild(element);
					});
					let list: any[];
					if (this.optionList != null) {
						list = data[this.optionList];
					} else {
						list = data;
					}
					list.forEach(info => {
						let option = document.createElement('option');
						option.setAttribute('value', this.getValue(info, this.optionValue));
						option.textContent = this.getValue(info, this.optionLabel);
						option.classList.add('koy-input-fetch-option');
						this.inputElement.appendChild(option);
					});
				});
				return true;
			}
		}, this);
	}

	/**
	 * optionタグを追加します。
	 * 
	 * @param value
	 * @param label
	 */
	addOption(value: string, label: string): HTMLOptionElement {
		let option = document.createElement('option');
		option.setAttribute('value', value);
		option.textContent = label;
		option.classList.add('koy-input-fetch-option');
		this.inputElement.appendChild(option);
		return option;
	}

	/**
	 * すべてのoptionタグを削除します。
	 */
	removeAllOptions(): void {
		KoyoriElement.querySelectorAll(this.inputElement, 'option').forEach(option => {
			option.parentElement.removeChild(option);
		});
	}

	/**
	 * @return 入力タグ
	 */
	protected getInputTag(): string {
		return 'select';
	}

	/**
	 * @return 入力タグのtype属性の値
	 */
	protected getInputType(): string {
		return null;
	}

	/**
	 * オブジェクトからパスに応じたフィールドの値を取得します。
	 * 
	 * @param object 
	 * @param path 
	 */
	private getValue(object: any, path: string): any {
		path.split('.').forEach(part => {
			object = object[part];
		});
		return object;
	}

	get optionList(): string {
		return this.getAttribute('option-list');
	}

	get optionLabel(): string {
		return this.getAttribute('option-label') || 'label';
	}

	get optionValue(): string {
		return this.getAttribute('option-value') || 'value';
	}
}
window.customElements.define('koy-select', KoyoriSelect);
